import { Injectable, signal } from '@angular/core';
import {
  HttpClient,
  HttpHeaders,
  HttpParams,
  HttpResponse,
} from '@angular/common/http';
import { Observable, map } from 'rxjs';
import { environment } from '../../environments/environment';
import { AuthService } from './auth.service';
import {
  QuotePrintRequest,
  QuotePrintResponse,
  QuoteRequest,
  QuoteResponse,
} from '../interfaces/cotizadores.interface';
import { ApiResponse } from '../interfaces/common.interfaces';

/**
 * Interfaz que define la estructura de un cotizador
 */
export interface Cotizador {
  description: string;
  url: string;
}

/**
 * Describe la licencia del productor expuesta por el dashboard
 */
export interface ProducerLicenceData {
  expirationDate: string;
  expiredLicence: string;
  showExpirationWarning: boolean;
}

/**
 * Servicio que gestiona las operaciones relacionadas con cotizadores
 */
@Injectable({
  providedIn: 'root',
})
export class CotizadoresService {
  private apiUrl = environment.int365_dom + '/api/v2';
  private tasaCambio: number = 0;
  private readonly producerLicenceWritable = signal<ProducerLicenceData>({
    expirationDate: '',
    expiredLicence: '',
    showExpirationWarning: false,
  });

  /**
   * Refleja la licencia del intermediario; se actualiza al completar getCotizadores
   */
  readonly producerLicence = this.producerLicenceWritable.asReadonly();

  private producerStatus = '';

  constructor(private http: HttpClient, private authService: AuthService) {}

  /**
   * Abre un cotizador en una nueva ventana después de realizar el pre-login
   * @param cotizador Objeto cotizador que contiene la URL y descripción
   * @throws Error si las credenciales están incompletas o hay error al abrir el cotizador
   */
  async abrirCotizador(cotizador: Cotizador): Promise<void> {
    try {
      const credentials = this.authService.getUserCredentials();

      if (!credentials.token) {
        throw new Error('Credenciales incompletas');
      }

      const preloggedUrl = this.buildPreloggedUrl(
        cotizador.url + '?SourceClientlink=PortalIntermediarios',
        credentials.token
      );

      window.open(preloggedUrl, '_blank');
    } catch (error) {
      console.error('Error al abrir cotizador:', error);
      throw error;
    }
  }

  /**
   * Obtiene las cotizaciones según los criterios especificados
   * @param request Objeto con los criterios de búsqueda
   * @returns Observable con la respuesta de la API conteniendo las cotizaciones
   */
  getQuotes(
    request: QuoteRequest
  ): Observable<HttpResponse<ApiResponse<QuoteResponse[]>>> {
    return this.http.get<ApiResponse<QuoteResponse[]>>(
      `${this.apiUrl}/quotes`,
      {
        ...this.getHttpOptions(request),
        observe: 'response',
        responseType: 'json',
      }
    );
  }

  /**
   * Obtiene la información de impresión de una cotización
   * @param request Objeto con los datos necesarios para imprimir la cotización
   * @returns Observable con la respuesta de la API conteniendo la información de impresión
   */
  getQuotePrint(
    request: QuotePrintRequest
  ): Observable<ApiResponse<QuotePrintResponse>> {
    return this.http
      .get<ApiResponse<QuotePrintResponse>>(
        `${this.apiUrl}/documents`,
        this.getHttpOptions(request)
      )
      .pipe(
        map((response) => {
          if (response.statusCode === 404) {
            throw { status: 404, error: response };
          }

          return response;
        })
      );
  }

  /**
   * Configura las opciones HTTP incluyendo headers y parámetros
   * @param params Parámetros opcionales para la petición
   * @returns Objeto con las opciones HTTP configuradas
   * @throws Error si no se encuentra el token
   */
  private getHttpOptions(params?: any) {
    const token = this.authService.getToken();
    if (!token) throw new Error('No token found');

    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    });

    return {
      headers,
      params: params
        ? new HttpParams({ fromObject: this.formatParams(params) })
        : undefined,
    };
  }

  /**
   * Formatea los parámetros para las peticiones HTTP
   * @param params Objeto con los parámetros a formatear
   * @returns Objeto con los parámetros formateados
   */
  private formatParams(params: any): { [key: string]: string } {
    const formattedParams: { [key: string]: string } = {};

    Object.entries(params).forEach(([key, value]) => {
      if (value === null || value === undefined) return;

      if (value instanceof Date) {
        formattedParams[key] = value.toISOString().split('T')[0];
      } else {
        formattedParams[key] = String(value);
      }
    });

    return formattedParams;
  }

  /**
   * Construye una URL prelogueada añadiendo el token como parámetro
   * @param baseUrl URL base del cotizador
   * @param token Token de autenticación
   * @returns URL prelogueada con el token incluido
   */
  private buildPreloggedUrl(baseUrl: string, token: string): string {
    const url = new URL(baseUrl);

    const fragmento = url.hash;
    url.hash = '';

    const parametrosExistentes: [string, string][] = [];
    url.searchParams.forEach((valor, clave) => {
      if (clave !== 'token') {
        parametrosExistentes.push([clave, valor]);
      }
    });

    url.search = '';

    parametrosExistentes.forEach(([clave, valor]) => {
      url.searchParams.append(clave, valor);
    });

    const baseUrlWithFragment = url.toString() + fragmento;

    const separator = baseUrlWithFragment.includes('?') ? '&' : '?';
   
    return `${baseUrlWithFragment}${separator}tk=${token}`;
  }

  /**
   * Obtiene la lista de cotizadores disponibles
   * @returns Observable con el array de cotizadores
   * @throws Error si el usuario no está autenticado o no hay token
   */
  getCotizadores(): Observable<Cotizador[]> {
    if (!this.authService.isAuthenticated()) {
      throw new Error('Usuario no autenticado');
    }

    const token = this.authService.getToken();
    const producerNumber = this.authService.getProducerNumber();

    if (!token) {
      throw new Error('No token found');
    }

    const headers = new HttpHeaders({
      Authorization: `Bearer ${token}`,
    });

    const params = new HttpParams().set('producerNumber', producerNumber || '');

    return this.http
      .get<{
        data: {
          sidebars: Cotizador[];
          conversionRate: { rate: number };
          producerLicence: {
            expirationDate: string;
            expiredLicence: string;
            producerStatus?: string;
            showExpirationWarning?: boolean;
          };
        };
      }>(`${environment.int365_dom}/api/v1/Dashboard/dashboard`, {
        headers,
        params,
      })
      .pipe(
        map((response) => {
          this.tasaCambio = response.data.conversionRate.rate;
          const licence = response.data.producerLicence;
          this.producerLicenceWritable.set({
            expirationDate: licence.expirationDate,
            expiredLicence: licence.expiredLicence,
            showExpirationWarning: licence.showExpirationWarning ?? false,
          });
          this.producerStatus = licence.producerStatus || '';
          return response.data.sidebars;
        })
      );
  }

  /**
   * Obtiene la tasa de cambio actual
   * @returns Valor numérico de la tasa de cambio
   */
  getTasaCambio(): number {
    return this.tasaCambio;
  }

  /**
   * Obtiene la información de licencia del productor
   * @returns Objeto con la fecha de expiración, si la licencia está expirada y si debe mostrarse el aviso de vencimiento
   */
  getProducerLicence(): ProducerLicenceData {
    return this.producerLicenceWritable();
  }

  /**
   * Obtiene el estado operativo del productor (independiente de la vigencia de la licencia)
   * @returns Valor devuelto por el dashboard o cadena vacía
   */
  getProducerStatus(): string {
    return this.producerStatus;
  }
}
